import { OnboardingState } from "types/onboarding";
import { ProductName } from "types/general";
import { ApiAdminFunnelUser } from "utils/api/admin/user";
import {
  IGNORED_STATES,
  ONBOARDING_STATES_EWA,
  ONBOARDING_STATES_TAX,
} from "./constants";

const getOnboardingStateToDisplay = (onboardingState: OnboardingState) => {
  if (
    onboardingState === OnboardingState.unlinkedCoreFinancialInstitutionAccount
  ) {
    return OnboardingState.linkedCoreFinancialInstitutionAccount;
  }

  return onboardingState;
};

type CountAccumulatorItem = {
  [key: string]: number;
};

export const onboardingStateCountReducer =
  ({
    missingStates,
    productEntryPoint,
    sortByKey,
  }: {
    missingStates: Set<OnboardingState>;
    productEntryPoint: ProductName | null;
    sortByKey: string;
  }) =>
  (arr: CountAccumulatorItem[], user: ApiAdminFunnelUser) => {
    // Some states are more usefully collapsed into others for funnel purposes
    const onboardingStateToDisplay = getOnboardingStateToDisplay(
      user.onboardingState
    );
    const onboardingStates =
      productEntryPoint === ProductName.taxRefundAdvance
        ? ONBOARDING_STATES_TAX
        : ONBOARDING_STATES_EWA;
    const index = onboardingStates.indexOf(onboardingStateToDisplay);
    const userProductEntryPoint =
      user.productEntryPoint || ProductName.earnedWageAccess;
    const key = user[sortByKey] || "none";

    if (index === -1 && !IGNORED_STATES.includes(onboardingStateToDisplay)) {
      missingStates.add(onboardingStateToDisplay);

      return arr;
    }

    const countBySlug = arr[index] || ({} as CountAccumulatorItem);
    const currentCount = countBySlug[key] || 0;
    const currentTotalCount = countBySlug.all || 0;

    const newArr = arr.slice(0);
    const userCount =
      !productEntryPoint || productEntryPoint === userProductEntryPoint ? 1 : 0;

    newArr[index] = {
      ...countBySlug,
      [key]: currentCount + userCount,
      all: currentTotalCount + userCount,
    };

    if (
      onboardingStateToDisplay === OnboardingState.onboarded &&
      user.eligible
    ) {
      const onboardedEligibleIndex =
        onboardingStates.indexOf("onboarded_eligible");
      const currentEligibeCount = newArr[onboardedEligibleIndex][key] || 0;
      const currentEligibleTotalCount = newArr[onboardedEligibleIndex].all || 0;
      newArr[onboardedEligibleIndex] = {
        ...newArr[onboardedEligibleIndex],
        [key]: currentEligibeCount + userCount,
        all: currentEligibleTotalCount + userCount,
      };
    }

    return newArr;
  };

export const onboardingStateCountCumulativeReducer =
  ({
    keys,
    productEntryPoint,
    onboardedEligibleAdjustment,
  }: {
    keys: string[];
    productEntryPoint: ProductName | null;
    onboardedEligibleAdjustment: { [key: string]: number };
  }) =>
  (arr, countByKey, index) => {
    let prevIndex = 0;

    const onboardingStates =
      productEntryPoint === ProductName.taxRefundAdvance
        ? ONBOARDING_STATES_TAX
        : ONBOARDING_STATES_EWA;
    // Since onboarded_eligible isn't a real state, we need to skip it in the cumulative count
    if (
      onboardingStates.length - index - 1 ===
      onboardingStates.indexOf("onboarded")
    )
      prevIndex = 1;

    const isOnboardedEligible =
      onboardingStates.length - index - 1 ===
      onboardingStates.indexOf("onboarded_eligible");

    const previousCountByKey = arr[prevIndex] || {};
    const cumulativeCountByKey = Array.from(keys).reduce((cumulative, key) => {
      let count = countByKey[key] || 0;
      if (isOnboardedEligible) count += onboardedEligibleAdjustment[key] || 0;
      return {
        ...cumulative,
        [key]: (previousCountByKey[key] || 0) + count,
      };
    }, {});

    return [cumulativeCountByKey, ...arr];
  };
